import { Loader2 } from 'lucide-react';

import { Button } from '@nesso/ui/primitives/button';

import { PickPlan } from '@/features/subscription/pick-plan';
import { useResubscribe } from '@/features/subscription/resubscribe';
import {
    SheetBackButton,
    SheetBody,
    SheetClose,
    SheetCloseButton,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetPanel,
    SheetTitle,
} from '@/shared/ui/panels/sheet-panel';

import { ResubscribeSheetSummary } from './resubscribe-sheet/resubscribe-sheet-summary';

type ResubscribeSheetProps = {
    returnTo: string;
};

export function ResubscribeSheet({ returnTo }: ResubscribeSheetProps) {
    const { selectedPlanCode, selectPlan, clearSelection, confirm, isConfirming } =
        useResubscribe(returnTo);

    return (
        <SheetPanel
            className="px-0"
            type="subscription/resubscribe"
            side="right"
            size="small"
            showCloseButton={false}
            onInteractOutside={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
        >
            <SheetHeader variant="mini" bordered>
                {selectedPlanCode && (
                    <SheetBackButton onClick={clearSelection} aria-label="Go back" />
                )}
                <SheetTitle>{selectedPlanCode ? 'Summary' : 'Choose Plan'}</SheetTitle>
                <SheetDescription className="sr-only">
                    Choose a plan to resubscribe and continue using Nesso.
                </SheetDescription>
                <SheetClose asChild>
                    <SheetCloseButton />
                </SheetClose>
            </SheetHeader>
            {selectedPlanCode ? (
                <>
                    <SheetBody className="flex flex-1 flex-col gap-6 overflow-y-auto py-10">
                        <ResubscribeSheetSummary planCode={selectedPlanCode} />
                    </SheetBody>
                    <SheetFooter orientation="stacked">
                        <Button className="w-full" disabled={isConfirming} onClick={confirm}>
                            {isConfirming && <Loader2 className="animate-spin" />}
                            {isConfirming ? 'Confirming' : 'Confirm and Pay'}
                        </Button>
                    </SheetFooter>
                </>
            ) : (
                <SheetBody className="flex flex-1 flex-col gap-6 overflow-y-auto pt-7 pb-6">
                    <PickPlan onSelectPlan={selectPlan} />
                </SheetBody>
            )}
        </SheetPanel>
    );
}
